import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { categories, collectionsByDate, monthNames, toKey, weekdayNames, type CategoryId } from '../../data/calendrierCollectes';
interface CollectionCalendarProps {
  month: Date;
  selected: Date;
  onMonthChange: (month: Date) => void;
  onSelect: (date: Date) => void;
}
function buildGrid(month: Date): (Date | null)[] {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (Date | null)[] = Array.from({
    length: offset
  }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, monthIndex, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
export function CollectionCalendar({
  month,
  selected,
  onMonthChange,
  onSelect
}: CollectionCalendarProps) {
  const cells = buildGrid(month);
  const selectedKey = toKey(selected);
  const todayKey = toKey(new Date(2026, 7, 26));
  const legend = Object.values(categories);
  return <section aria-label="Calendrier des collectes" className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-7" data-id="element-709" data-mp="10133wffg5zj9">
      <div className="flex items-center justify-between" data-id="element-710" data-mp="w019cbwh6bnm">
        <button type="button" aria-label="Mois précédent" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-600 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-711" data-mp="vxewbwqtf3y0">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-712" data-mp="8ayiqejv58gz" />
        </button>
        <h2 aria-live="polite" className="text-base font-bold tracking-[0.14em] text-ville-navy" data-id="element-713" data-mp="3akn5vzg7qzu">
          {monthNames[month.getMonth()]} {month.getFullYear()}
        </h2>
        <button type="button" aria-label="Mois suivant" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-600 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-714" data-mp="9r2kdif4q6er">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-715" data-mp="3mz73x8zhzjsl" />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-slate-400" data-id="element-716" data-mp="38aaxthh04gum">
        {weekdayNames.map(day => <span key={day} data-id="element-717" data-mp="2z7i7bgfn895u">{day}</span>)}
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1" data-id="element-718" data-mp="2za4ma14v5qy3">
        {cells.map((date, index) => {
        if (!date) return <span key={`empty-${index}`} aria-hidden="true" data-id="element-719" data-mp="348xube1dspqv" />;
        const key = toKey(date);
        const events: CategoryId[] = collectionsByDate[key] ?? [];
        const isSelected = key === selectedKey;
        const isToday = key === todayKey;
        return <button key={key} type="button" onClick={() => onSelect(date)} aria-pressed={isSelected} aria-label={`${date.getDate()} ${monthNames[date.getMonth()].toLowerCase()}${events.length ? `, ${events.length} collecte(s)` : ', aucune collecte'}`} className={`flex h-14 flex-col items-center justify-center gap-1 rounded-2xl border text-sm transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${isSelected ? 'border-ville-indigo bg-ville-indigo-soft font-bold text-ville-indigo' : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50'}`} data-id="element-720" data-mp="2tnc9vwhcs4kn" data-mpi={index}>
              <span className={isToday && !isSelected ? 'font-bold text-ville-navy underline underline-offset-4' : ''} data-id="element-721" data-mp="1klj0vf16d8d6">
                {date.getDate()}
              </span>
              <span className="flex h-1.5 items-center gap-0.5" data-id="element-722" data-mp="1pd0ypu7wistx">
                {events.slice(0, 4).map(id => <span key={id} className={`h-1.5 w-1.5 rounded-full ${categories[id].dot}`} aria-hidden="true" data-id="element-723" data-mp="1tfnlhgpptnk9" />)}
              </span>
            </button>;
      })}
      </div>

      <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 border-t border-slate-100 pt-5" data-id="element-724" data-mp="15o8evbqwok37">
        {legend.map(category => <li key={category.id} className="flex items-center gap-2 text-xs text-slate-500" data-id="element-725" data-mp="1anld0sn89dl3">
            <span className={`h-2 w-2 rounded-full ${category.dot}`} aria-hidden="true" data-id="element-726" data-mp="2h3x3s8npzt6n" />
            {category.label}
          </li>)}
      </ul>
    </section>;
}